import { useEffect, useRef } from 'react';
import { pageKeyAtom } from '@src/atoms/pageMeta';
import { beginPresetSession, endPresetSession } from '@src/utils/presets/transientPreset';
import { batch, useStore } from 'react-redux';
import { useStore as useAtomStore } from 'jotai';
import { cloneDeep } from 'lodash';
import metricsStateSelector from '@src/store/metricsChartContainer/selectors';
import baseSelector from '@src/store/trends/base/selectors';
import { summaryWidgetsSelector } from '@src/store/summaryWidgets/selector';
import { initialState as defaultSummary } from '@src/store/summaryWidgets/reducer';
import { DEFAULT_DIMENSIONS } from '@src/store/trends/base/reducer';
import { DEFAULT_METRIC } from '@src/utils/constants';
import * as metricAtoms from '@src/components/metrics-panel-hz/atoms';
import {
  activeTabAtom,
  dimensionSortsAtom,
  heightAtom,
  tableMetricsAtom,
} from '@src/components/dimension-panel-hz/atoms';
import { summaryPanelAtom, metricPanelAtom, dimensionPanelAtom } from '@src/atoms/panels';
import {
  getWidgetStateForPersistence,
  useDashboardStore,
} from '@src/components/widget-panel/state/dashboard';
import { getDefaultDashboardConfig } from '@src/components/widget-panel/config/utils';
import { buildQueryConfig } from '@src/components/widget-panel/state/utils';
import { clearQueryQueue } from '@src/components/widget-panel/state/queryManager';
import { APPLY_PRESET_VIEW, PresetViewConfig } from '@src/utils/presets/personalPresets';

const metricFields = {
  layout: metricAtoms.layoutAtom,
  unit: metricAtoms.unitAtom,
  diagnosticsMode: metricAtoms.diagnosticsModeAtom,
  yScaleMode: metricAtoms.yScaleModeAtom,
  logAxis: metricAtoms.logAxisAtom,
  primaryFilterVisibility: metricAtoms.primaryFilterVisibilityAtom,
  activeTab: metricAtoms.activeTabAtom,
  widgetPanelVisibility: metricAtoms.widgetPanelVisibilityAtom,
  percentile: metricAtoms.percentileAtom,
};

export function getDefaultView(): PresetViewConfig {
  const widgets = getDefaultDashboardConfig().widgets;
  return cloneDeep({
    metricsChartContainer: { metric: DEFAULT_METRIC, metrics: [DEFAULT_METRIC] },
    base: {
      dimensions: DEFAULT_DIMENSIONS.EI,
      dimensionMetrics: [DEFAULT_METRIC],
      globalOrderBy: DEFAULT_METRIC,
    },
    metricPanel: {
      layout: '4s',
      unit: '%',
      diagnosticsMode: false,
      yScaleMode: false,
      logAxis: false,
      primaryFilterVisibility: true,
      activeTab: metricAtoms.Tabs.MetricCharts,
      widgetPanelVisibility: false,
      percentile: 'avg',
    },
    dimensionPanel: { activeTab: 0, sorts: {}, height: 's' },
    sections: { summary: true, metric: true, dimension: true },
    summaryWidgets: defaultSummary,
    widgetState: { widgets, order: Object.keys(widgets) },
  });
}

export function usePresetView(onCommit?: () => void) {
  const redux = useStore();
  const atoms = useAtomStore();
  const owner = useRef(Symbol());
  useEffect(() => {
    const token = owner.current;
    return () => endPresetSession(token);
  }, []);
  const capture = (): PresetViewConfig => {
    const state = redux.getState();
    const { metric, metrics } = metricsStateSelector(state);
    const { dimensions, dimensionMetrics, globalOrderBy } = baseSelector(state);
    return cloneDeep({
      metricsChartContainer: { metric, metrics },
      base: { dimensions, dimensionMetrics, globalOrderBy },
      metricPanel: Object.fromEntries(
        Object.entries(metricFields).map(([key, atom]) => [key, atoms.get(atom as any)]),
      ),
      dimensionPanel: {
        activeTab: atoms.get(activeTabAtom),
        sorts: atoms.get(dimensionSortsAtom),
        height: atoms.get(heightAtom),
      },
      sections: {
        summary: atoms.get(summaryPanelAtom),
        metric: atoms.get(metricPanelAtom),
        dimension: atoms.get(dimensionPanelAtom),
      },
      summaryWidgets: summaryWidgetsSelector(state),
      widgetState: getWidgetStateForPersistence(),
    });
  };
  const apply = (input: PresetViewConfig, original?: PresetViewConfig) => {
    const view = cloneDeep(input);
    // Prepare everything that can fail before touching live state.
    const queryManager = buildQueryConfig();
    clearQueryQueue();
    if (original) beginPresetSession(owner.current, atoms.get(pageKeyAtom), original, onCommit);
    else endPresetSession(owner.current);
    batch(() => {
      // A single Redux action avoids the primary-metric saga resetting columns/sorting.
      redux.dispatch({ type: APPLY_PRESET_VIEW, view });
      Object.entries(metricFields).forEach(([key, atom]) =>
        atoms.set(atom as any, view.metricPanel[key]),
      );
      atoms.set(tableMetricsAtom, {
        metrics: Array.from(
          new Set([...view.base.dimensionMetrics, view.base.globalOrderBy]),
        ).sort(),
        orderBy: view.base.globalOrderBy,
      });
      atoms.set(activeTabAtom, view.dimensionPanel.activeTab);
      atoms.set(dimensionSortsAtom, view.dimensionPanel.sorts);
      atoms.set(heightAtom, view.dimensionPanel.height);
      atoms.set(summaryPanelAtom, view.sections.summary);
      atoms.set(metricPanelAtom, view.sections.metric);
      atoms.set(dimensionPanelAtom, view.sections.dimension);
      useDashboardStore.setState({ ...view.widgetState, queryManager });
      // Queries subscribe to the completed view. REFRESH_TRENDS re-applies a filter
      // and would run context-changing sagas, so it must not be used here.
    });
  };
  return { capture, apply };
}
